import { useState } from 'react'
import { useApiQuery, type Column } from 'grabbitbot-legacy-ui'
import { Table } from '../../components/DataTable'
import { money, products as productsApi } from '../../api/fee'
import type { Product } from '../../api/types'
import { ConfirmButton, Notice, PageTitle, QueryState } from '../../components/common'
import { useProject } from '../../layout/projectContext'
import { formatDateTime, formatPrice } from '../../lib/format'
import { useEmailParam } from '../../lib/useEmailParam'
import { useMoneyAction } from '../../lib/useMoneyAction'
import { AdminUserLocalMenu } from './AdminUserLocalMenu'

/**
 * Port of `AdminUserProductListComponent` (`…/admin/users/products?email=`):
 * the products an end-user paid, and "mark another product as paid without
 * payment" (`POST …/admin/payment/product?email=&product=`, legacy confirm
 * text kept, now inline). BUSINESS-CRITICAL: grants a paid product with no
 * charge — double-submit guarded; a pay-once product that is already paid
 * cannot be marked again from here (legacy only printed "already paid!").
 */
export function AdminUserProductListPage() {
  const { projectId, project } = useProject()
  const email = useEmailParam()
  const mine = useApiQuery(() => (email ? productsApi.productsOfUser(projectId, email) : Promise.resolve([])), [projectId, email])
  const all = useApiQuery(() => productsApi.search(projectId), [projectId])
  const [showAll, setShowAll] = useState(false)
  const actions = useMoneyAction()
  const paid = new Map((mine.data ?? []).map((p) => [p.id, p]))

  const markPaid = (product: Product) =>
    actions.run(`mark-${product.id}`, `${email}|${product.id}`, () => money.markProductPaidForUser(projectId, email, product.id), () => {
      actions.setFeedback(`mark-${product.id}`, { tone: 'success', text: `"${product.name}" marked as paid.` })
      mine.refetch()
    })

  const columns: Column<Product>[] = [
    { key: 'name', label: 'Product' },
    {
      key: 'price',
      label: 'Price',
      align: 'right',
      numeric: true,
      format: (_v, p) => (
        <>
          {formatPrice(p.price, p.currency)}
          <span className="portal-muted-small">{Number(p.limit) === 0 ? 'payable anytime' : 'payable once'}</span>
        </>
      ),
    },
    {
      key: 'payedAt',
      label: 'Paid',
      sortValue: (p) => p.onetimeproduct?.payedAt ?? '',
      format: (_v, p) =>
        p.onetimeproduct?.payedAt
          ? `${Number(p.limit) === 0 ? 'Last paid at' : 'Already paid at'} ${formatDateTime(p.onetimeproduct.payedAt)}`
          : '',
    },
  ]

  return (
    <div className="portal-card">
      <PageTitle title={`Products — ${project.name}`}>
        <AdminUserLocalMenu email={email} title="Products" />
      </PageTitle>
      {!email && <p className="portal-text-muted">Pick a user from the menu above.</p>}
      <QueryState loading={mine.loading} error={mine.error}>
        <div className="portal-table-scroll">
          <Table
            columns={columns}
            rows={mine.data ?? []}
            rowKey={(p) => p.id}
            rowClassName={(p) => (Number(p.limit) === 1 ? 'fs-row--success' : 'fs-row--info')}
          />
        </div>
        {mine.data?.length === 0 && email && <p className="portal-text-muted">No paid products.</p>}
      </QueryState>
      {email && (
        <section className="fs-panel">
          <button type="button" aria-expanded={showAll} onClick={() => setShowAll(!showAll)}>
            Mark another product as paid without payment
          </button>
          {showAll && (
            <ul className="fs-plain-list">
              {(all.data ?? []).map((p) => {
                const already = paid.has(p.id)
                const blocked = already && Number(p.limit) === 1
                return (
                  <li key={p.id} className="portal-inline-actions">
                    <ConfirmButton
                      label={`Mark ${p.name} (${formatPrice(p.price, p.currency)}) as paid NOW${already ? ' - already paid!' : ''}`}
                      pendingText={`Are you sure you want to mark "${p.name}" for ${email} paid without payment?`}
                      busy={actions.isBusy(`mark-${p.id}`)}
                      disabled={blocked}
                      onConfirm={() => void markPaid(p)}
                    />
                    <Notice feedback={actions.feedbackFor(`mark-${p.id}`)} />
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      )}
    </div>
  )
}
